import React, { useState, useEffect, useContext } from "react"
import { useCookies } from "react-cookie"
import { Helmet } from "react-helmet"
import styled, { css, ThemeContext } from "styled-components"
import { useForm, Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useStaticQuery, graphql } from "gatsby"
import { subscribeToNewsletter } from "../context/utils/events/klaviyo"
import { subscribeToNewsletter as pinterestTagSubscribeToNewsletter } from "../context/utils/events/pinterestTag"
import { BrinkContext } from "../context/BrinkContext"
import { boxShadow, MEDIA_MIN_MEDIUM } from "../../constants"
import BackgroundImage from "gatsby-background-image"
import Input from "../ui/Input"
import Button from "../ui/Button"
import Loader from "../ui/Loader"
import Backdrop from "../ui/Backdrop"
import dayjs from "dayjs"

const Container = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  background: ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.black};
  width: calc(100% - 4rem);
  max-width: 85rem;
  box-shadow: ${boxShadow};
  transition: opacity 0.3s;
  font-size: 1.6rem;
  flex-wrap: wrap;
  padding: 2rem;
  z-index: 10003;

  ${MEDIA_MIN_MEDIUM} {
    right: auto;
    bottom: auto;
    flex-wrap: nowrap;
  }

  a {
    margin-left: 0.5rem;
    white-space: nowrap;
  }
`

const Image = styled(BackgroundImage)`
  display: none;
  background-size: cover !important;
  background-position: top center !important;

  ${MEDIA_MIN_MEDIUM} {
    width: 45%;
    min-height: 40rem;
    display: block;
  }
`

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  ${MEDIA_MIN_MEDIUM} {
    width: 55%;
    min-height: 40rem;
    padding-left: 2rem;
  }

  > div {
    width: 100%;
  }

  p {
    text-align: center;
    margin: 1rem auto 1rem;
    font-size: 1.2rem;
    line-height: 2rem;

    ${MEDIA_MIN_MEDIUM} {
      font-size: 1.3rem;
    }
  }
`

const Heading = styled.h3`
  width: 100%;
  text-align: center;
  margin: 4rem 0 0;
  font-size: 1.8rem;
  line-height: 2.2rem;
  font-weight: 700;

  ${MEDIA_MIN_MEDIUM} {
    font-size: 2rem;
    line-height: 2.4rem;
  }
`

const Form = styled.form`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`

const EmailField = styled(Input)`
  width: 100%;
  margin-bottom: 1rem;

  input {
    border: 0.1rem solid ${(p) => p.theme.colors.lightGrey};
    background-color: transparent;
    color: ${(p) => p.theme.colors.black};
    text-transform: initial;
  }
`

const PopupButton = styled(Button)`
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.white};
  width: 100%;
`

const MessageContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  font-size: 1.2rem;
  padding: 2rem 0 1rem;
  line-height: 2rem;
  height: 8rem;
  margin: 0 auto;
  letter-spacing: 0.05rem;

  i {
    font-size: 1.8rem;
    margin-right: 0.5rem;
    padding-top: 0.1rem;
  }

  a {
    display: none;
  }
`

const Close = styled.div`
  position: absolute;
  width: 4rem;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
  font-size: 2rem;
`

const Disclaimer = styled.span`
  font-size: 1rem;
  padding: 1rem 0 0;
  color: ${(p) => p.theme.colors.darkGrey};
  text-align: center;

  ${MEDIA_MIN_MEDIUM} {
    padding: 3rem 0 0;
  }
`

const ContinueLink = styled.div`
  text-align: center;
  width: 100%;
  margin: 3rem 0 2rem;

  ${MEDIA_MIN_MEDIUM} {
    margin: 0;
  }
`

const Link = styled.span`
  text-decoration: none;
  cursor: pointer;
  position: relative;
  color: ${(p) => p.color || "white"};
  font-size: 1.4rem;

  ${(p) =>
    p.underline &&
    css`
      &:after {
        content: "";
        position: absolute;
        height: 1px;
        left: 0;
        right: 0;
        bottom: -0.3rem;
        background-color: ${(p) => (p.inverted ? "transparent" : p.color)};
        transition: background-color 0.2s;
      }

      &:hover {
        &:after {
          background-color: ${(p) => (p.inverted ? p.color : "transparent")};
        }
      }
    `}
`

const DiscountCode = styled.div`
  display: flex;
  width: 100%;
  height: 8rem;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin-top: 3rem;

  &:before {
    content: "";
    position: absolute;
    border: 0.8rem dashed rgba(64, 112, 96, 0.3);
    top: -0.7rem;
    bottom: -0.7rem;
    left: -0.7rem;
    right: -0.7rem;
  }
`

const NewsletterPopup = () => {
  const { t } = useTranslation("translations")
  const { control, errors, handleSubmit } = useForm()
  const { colors } = useContext(ThemeContext)
  const [_cookies, _setCookie] = useCookies(["newsletterPopup"])
  const [klaviyoResponse, setKlaviyoResponse] = useState()

  const {
    isLoading,
    setIsLoading,
    languageCode,
    currentStore,
    showNewsletterPopup,
    setShowNewsletterPopup,
    setDiscountCodePopup,
    discountCodePopup,
    addDiscount,
    isCartClosed
  } = useContext(BrinkContext)

  const { sanityConfigNewsletterPopup } = useStaticQuery(graphql`
    query {
      sanityConfigNewsletterPopup {
        enabled
        enabledOnPageLoad
        disclaimer {
          en
          de
        }
        discountCode
        title {
          en
          de
        }
        subTitle {
          en
          de
        }
        buttonText {
          en
          de
        }
        declineText {
          en
          de
        }
        successSubTitle {
          en
          de
        }
        successTitle {
          en
          de
        }
        shopNowText {
          en
          de
        }
        image {
          asset {
            gatsbyImageData
          }
        }
      }
    }
  `)

  const {
    enabled,
    enabledOnPageLoad,
    buttonText,
    disclaimer,
    discountCode,
    shopNowText,
    subTitle,
    successSubTitle,
    successTitle,
    title,
    image
  } = sanityConfigNewsletterPopup || {}

  useEffect(() => {
    if (!_cookies.newsletterPopup && enabledOnPageLoad)
      setShowNewsletterPopup(true)
  }, [_cookies, setShowNewsletterPopup, enabledOnPageLoad])

  if (!enabled) return null

  const close = () => {
    _setCookie("newsletterPopup", 1, {
      path: "/",
      expires: dayjs().add(90, "days").toDate()
    })
    setShowNewsletterPopup(false)
  }

  const onSubmit = async (data) => {
    setIsLoading(true)
    const response = await subscribeToNewsletter(
      currentStore.countryCode,
      data.popupEmail
    )
    setKlaviyoResponse(response)
    if (response.success) {
      pinterestTagSubscribeToNewsletter()
      setDiscountCodePopup(discountCode)
      if (!isCartClosed()) {
        await addDiscount({ code: discountCode, checkout: false })
      }
    }
    setIsLoading(false)
  }

  return (
    showNewsletterPopup && (
      <>
        <Backdrop active handleOnClick={() => setShowNewsletterPopup(false)} />
        <Container>
          <Loader isLoading={isLoading} />
          <Helmet>
            <html lang="en" className="disableScroll" />
            <body className="disableScroll" />
          </Helmet>
          <Close onClick={() => close()}>
            <i className="fal fa-times"></i>
          </Close>
          <Image fluid={image.asset.fluid} />
          {klaviyoResponse && klaviyoResponse.success ? (
            <Content>
              <div>
                <Heading>
                  {successTitle[languageCode] || successTitle.en}
                </Heading>
                <p>{successSubTitle[languageCode] || successSubTitle.en}</p>
              </div>
              <DiscountCode>{discountCodePopup}</DiscountCode>
              <ContinueLink>
                <Link color={colors.black} underline onClick={() => close()}>
                  {shopNowText[languageCode] || shopNowText.en}
                </Link>
              </ContinueLink>
            </Content>
          ) : (
            <Content>
              <div>
                <Heading>{title[languageCode] || title.en}</Heading>
                <p>{subTitle[languageCode] || subTitle.en}</p>
              </div>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  as={EmailField}
                  placeholder={t("Your e-mail")}
                  control={control}
                  name="popupEmail"
                  type="popupEmail"
                  rules={{
                    required: t("This is a required field"),
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: t("Please supply a valid e-mail address")
                    }
                  }}
                />

                <PopupButton type="submit">
                  {buttonText[languageCode] || buttonText.en}
                </PopupButton>
              </Form>
              {Object.keys(errors).length !== 0 ||
                (klaviyoResponse && (
                  <MessageContainer>
                    {Object.keys(errors).length !== 0 ? (
                      <>
                        <i className="fal fa-exclamation-circle"></i>
                        {errors.email.message}
                      </>
                    ) : (
                      klaviyoResponse &&
                      klaviyoResponse.data && (
                        <>
                          {klaviyoResponse.data.is_subscribed ? (
                            <>
                              <i className="fal fa-exclamation-circle"></i>{" "}
                              {t("This email address has already subscribed")}
                            </>
                          ) : klaviyoResponse.success ? (
                            <>
                              <i className="fal fa-check-circle"></i>{" "}
                              {t("Thank you for subscribing!")}
                            </>
                          ) : (
                            <>
                              <i className="fal fa-exclamation-circle"></i>{" "}
                              {t("An error occured, please try again later.")}
                            </>
                          )}
                        </>
                      )
                    )}
                  </MessageContainer>
                ))}
              <DiscountCode>{t("Your code will show here")}</DiscountCode>
              <Disclaimer>
                {disclaimer[languageCode] || disclaimer.en}
              </Disclaimer>
            </Content>
          )}
        </Container>
      </>
    )
  )
}

export default NewsletterPopup
